import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, share, switchMap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { CaseManagementService } from '../../services/case-management.service';
import { UtilsService } from '../../edge/utils.service';
import { SharedActions } from '@states/shared/shared.action-types';
import { CaseFileRequestActions } from '@states/case-file-request/case-file-request.actions';
import { CaseManagementActions } from '@states/case-management/case-management.actions';

@Injectable()
export class CaseFileRequestEffect {
  private readonly actions$ = inject(Actions);
  private readonly caseManagementService = inject(CaseManagementService);
  private readonly utilService = inject(UtilsService);
  private readonly translateService = inject(TranslateService);

  public getFileRequests$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaseFileRequestActions.getFileRequests),
      switchMap(({ caseId }) =>
        this.caseManagementService.getCaseFileRequests(caseId).pipe(
          map((fileRequests) =>
            CaseFileRequestActions.getFileRequestsSuccess({ fileRequests }),
          ),
          catchError((error) => [
            CaseFileRequestActions.getFileRequestsFail(),
            SharedActions.showMessage({
              error: this.utilService.errMessage(error),
            }),
          ]),
        ),
      ),
      share(),
    ),
  );

  public createFileRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaseFileRequestActions.createFileRequest),
      switchMap(({ caseId, request }) =>
        this.caseManagementService.addUploadPermission(caseId, request).pipe(
          mergeMap(() => [
            CaseFileRequestActions.createFileRequestSuccess(),
            CaseFileRequestActions.getFileRequests({ caseId }),
            CaseManagementActions.getCaseFeatureCounters({ id: caseId }),
            SharedActions.showMessage({
              success: this.getRecipientsToastMessage(
                request.emailDetails.map(({ email }) => email),
              ),
            }),
          ]),
          catchError((error) => [
            CaseFileRequestActions.createFileRequestFail(),
            SharedActions.showMessage({
              error: this.utilService.errMessage(error),
            }),
          ]),
        ),
      ),
      share(),
    ),
  );

  public revokeFileRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaseFileRequestActions.revokeFileRequest),
      switchMap(({ caseId, fileRequestId }) =>
        this.caseManagementService
          .revokeCaseFileRequest(caseId, fileRequestId)
          .pipe(
            mergeMap(() => [
              CaseFileRequestActions.revokeFileRequestSuccess({
                id: fileRequestId,
              }),
              CaseManagementActions.getCaseFeatureCounters({ id: caseId }),
              SharedActions.showMessage({
                success: this.translateService.instant(
                  'CASES.FILE_REQUEST.TOASTS.REVOKED',
                ),
              }),
            ]),
            catchError((error) => [
              CaseFileRequestActions.revokeFileRequestFail({
                id: fileRequestId,
              }),
              SharedActions.showMessage({
                error: this.utilService.errMessage(error),
              }),
            ]),
          ),
      ),
      share(),
    ),
  );

  private getRecipientsToastMessage(recipients: string[]): string {
    const uniqueRecipients = [...new Set(recipients.filter(Boolean))];

    if (uniqueRecipients.length === 1) {
      return this.translateService.instant(
        'CASES.TOASTS.UPLOAD_PERMISSION_ADDED_SINGLE',
        {
          recipient: uniqueRecipients[0],
        },
      );
    }

    return this.translateService.instant(
      'CASES.TOASTS.UPLOAD_PERMISSION_ADDED_MULTIPLE',
      {
        count: uniqueRecipients.length,
      },
    );
  }
}
